import { BigNum, OrderAction, OrderType, User } from '@drift-labs/sdk';
import { matchEnum } from '@drift/common';
import { EMPTY_TABLE_VALUE } from 'src/components/Tables/TableV2';

export const getOrderStatusString = (
	action: OrderAction,
	totalBaseAmountFilled: BigNum,
	baseAssetAmount: BigNum
) => {
	return matchEnum(action, OrderAction.CANCEL)
		? totalBaseAmountFilled.gtZero()
			? 'Partially Filled (Cancelled)'
			: 'Cancelled'
		: matchEnum(action, OrderAction.FILL) &&
			  baseAssetAmount.eq(totalBaseAmountFilled)
			? 'Filled'
			: matchEnum(action, OrderAction.PLACE)
				? 'Open'
				: matchEnum(action, OrderAction.EXPIRE)
					? 'Expired'
					: totalBaseAmountFilled.gtZero()
						? 'Partially Filled'
						: 'Triggered';
};

export const getTriggerPriceFromOrder = (
	orderRecord: {
		orderType: OrderType;
		triggerPrice: BigNum;
	},
	defaultValue = EMPTY_TABLE_VALUE
) => {
	const orderType = orderRecord.orderType;
	if (
		matchEnum(orderType, OrderType.TRIGGER_LIMIT) ||
		matchEnum(orderType, OrderType.TRIGGER_MARKET)
	) {
		return orderRecord.triggerPrice.toNotional(true);
	} else {
		return defaultValue;
	}
};

// Buffer to store the 10 most recently used user order IDs. Helps ensure that
// we don't re-use any order IDs while previous ones may still be lingering in
// state. We need this because the toast logic doesn't clear the toast as soon
// as the order is filled. So without this, we might re-use an order ID if the
// user makes a new order while the previous toast is still mounted, which would
// then break because the toast IDs would clash. Hacky because this fn shouldn't
// need to know anything about toast IDs but can't currently think of a better way.
const RECENT_USER_ORDER_ID_BUFFER: number[] = [];

export const generateUserOrderIdForUser = (user: User) => {
	if (!user) {
		return 0;
	}

	const existingOrderIds = new Set([
		...RECENT_USER_ORDER_ID_BUFFER,
		...user.getOpenOrders().map((order) => order.userOrderId),
	]);

	// Need to pick a number < 256 that is not in the existingOrderIds array
	let foundNextUserOrderId = false;
	let nextUserOrderId = 0;

	while (!foundNextUserOrderId) {
		if (!existingOrderIds.has(nextUserOrderId)) {
			foundNextUserOrderId = true;
		} else {
			nextUserOrderId++;
		}

		if (nextUserOrderId >= 256) {
			throw new Error('No more user order ids available');
		}
	}

	// Add the new user order ID to the buffer
	RECENT_USER_ORDER_ID_BUFFER.push(nextUserOrderId);
	// Keep only the 10 most recent user order IDs
	RECENT_USER_ORDER_ID_BUFFER.splice(
		0,
		RECENT_USER_ORDER_ID_BUFFER.length - 10
	);

	return nextUserOrderId;
};

export const getNextUserOrderIdForUser = (user: User) => {
	if (!user) {
		return 0;
	}

	return user.getUserAccount().nextOrderId;
};
